/**
 * Similarity-transform estimation from point correspondences (Umeyama 1991):
 * given n >= 3 pairs of corresponding 3D points, find rotation R, uniform
 * scale s and translation t minimising sum |s*R*src_i + t - dst_i|^2.
 *
 * Used by the align tool to register two separately captured splat models
 * (e.g. drone exterior + portal-cam interior) from user-clicked landmark
 * pairs. Kept free of playcanvas/DOM imports so it runs under plain node
 * for unit tests (tools/align.test.ts).
 */

type Point3 = [number, number, number];

// 3x3 matrices are stored row-major as number[9]: m[row * 3 + col].
type Mat3 = number[];

type SimilarityTransform = {
    rotation: Mat3;            // row-major rotation matrix, det = +1
    scale: number;             // uniform scale s
    translation: Point3;       // t, such that dst ≈ s * R * src + t
    rmse: number;              // sqrt(mean squared residual) over all pairs
    residuals: number[];       // per-pair |s*R*src_i + t - dst_i|
};

// insufficient-points: fewer than 3 pairs.
// degenerate: the source points span less than a plane (coincident or
// collinear) — the rotation about that line/point is undetermined.
type SimilarityError = 'insufficient-points' | 'degenerate';

type SimilarityResult = SimilarityTransform | { error: SimilarityError };

const isSimilarityError = (r: SimilarityResult): r is { error: SimilarityError } => 'error' in r;

const mulMat3Point = (m: Mat3, p: Point3): Point3 => [
    m[0] * p[0] + m[1] * p[1] + m[2] * p[2],
    m[3] * p[0] + m[4] * p[1] + m[5] * p[2],
    m[6] * p[0] + m[7] * p[1] + m[8] * p[2]
];

const applySimilarity = (t: SimilarityTransform, p: Point3): Point3 => {
    const r = mulMat3Point(t.rotation, p);
    return [
        r[0] * t.scale + t.translation[0],
        r[1] * t.scale + t.translation[1],
        r[2] * t.scale + t.translation[2]
    ];
};

const det3 = (m: Mat3): number => m[0] * (m[4] * m[8] - m[5] * m[7]) -
    m[1] * (m[3] * m[8] - m[5] * m[6]) +
    m[2] * (m[3] * m[7] - m[4] * m[6]);

// Eigendecomposition of a symmetric 3x3 matrix via cyclic Jacobi rotations.
// Returns eigenvalues (descending) and the matching orthonormal eigenvectors
// as the COLUMNS of v (row-major). Deterministic, no dependencies; plenty
// accurate for landmark counts in the dozens.
const symmetricEigen3 = (a: Mat3): { values: [number, number, number], vectors: Mat3 } => {
    const m = a.slice();
    // v starts as identity and accumulates the rotations
    const v: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

    for (let sweep = 0; sweep < 64; sweep++) {
        const off = Math.hypot(m[1], m[2], m[5]);
        const diag = Math.hypot(m[0], m[4], m[8]);
        if (off <= 1e-15 * Math.max(diag, 1e-300)) break;

        for (const [p, q] of [[0, 1], [0, 2], [1, 2]] as const) {
            const apq = m[p * 3 + q];
            if (apq === 0) continue;
            const app = m[p * 3 + p];
            const aqq = m[q * 3 + q];
            const theta = (aqq - app) / (2 * apq);
            // stable tan of the rotation angle
            const tan = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
            const c = 1 / Math.sqrt(tan * tan + 1);
            const s = tan * c;

            // m = J^T m J applied to rows/cols p and q
            for (let k = 0; k < 3; k++) {
                const mkp = m[k * 3 + p];
                const mkq = m[k * 3 + q];
                m[k * 3 + p] = c * mkp - s * mkq;
                m[k * 3 + q] = s * mkp + c * mkq;
            }
            for (let k = 0; k < 3; k++) {
                const mpk = m[p * 3 + k];
                const mqk = m[q * 3 + k];
                m[p * 3 + k] = c * mpk - s * mqk;
                m[q * 3 + k] = s * mpk + c * mqk;
            }
            for (let k = 0; k < 3; k++) {
                const vkp = v[k * 3 + p];
                const vkq = v[k * 3 + q];
                v[k * 3 + p] = c * vkp - s * vkq;
                v[k * 3 + q] = s * vkp + c * vkq;
            }
        }
    }

    // sort eigenpairs descending by eigenvalue
    const order = [0, 1, 2].sort((i, j) => m[j * 3 + j] - m[i * 3 + i]);
    const values = order.map(i => m[i * 3 + i]) as [number, number, number];
    const vectors: Mat3 = new Array(9);
    for (let col = 0; col < 3; col++) {
        const src = order[col];
        for (let row = 0; row < 3; row++) {
            vectors[row * 3 + col] = v[row * 3 + src];
        }
    }
    return { values, vectors };
};

const estimateSimilarity = (
    src: Point3[],
    dst: Point3[],
    opts?: { withScale?: boolean }
): SimilarityResult => {
    const n = src.length;
    if (n < 3 || dst.length !== n) {
        return { error: 'insufficient-points' };
    }
    const withScale = opts?.withScale ?? true;

    const muSrc: Point3 = [0, 0, 0];
    const muDst: Point3 = [0, 0, 0];
    for (let i = 0; i < n; i++) {
        for (let k = 0; k < 3; k++) {
            muSrc[k] += src[i][k] / n;
            muDst[k] += dst[i][k] / n;
        }
    }

    // cross-covariance cov = (1/n) Σ (dst_i - muDst)(src_i - muSrc)^T and
    // source variance sigmaSrc = (1/n) Σ |src_i - muSrc|^2
    const cov: Mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    let sigmaSrc = 0;
    for (let i = 0; i < n; i++) {
        const sx = src[i][0] - muSrc[0];
        const sy = src[i][1] - muSrc[1];
        const sz = src[i][2] - muSrc[2];
        const dx = dst[i][0] - muDst[0];
        const dy = dst[i][1] - muDst[1];
        const dz = dst[i][2] - muDst[2];
        cov[0] += dx * sx / n; cov[1] += dx * sy / n; cov[2] += dx * sz / n;
        cov[3] += dy * sx / n; cov[4] += dy * sy / n; cov[5] += dy * sz / n;
        cov[6] += dz * sx / n; cov[7] += dz * sy / n; cov[8] += dz * sz / n;
        sigmaSrc += (sx * sx + sy * sy + sz * sz) / n;
    }

    if (sigmaSrc < 1e-18) {
        return { error: 'degenerate' };
    }

    // SVD of cov via eigendecomposition: cov^T cov = V D^2 V^T
    const covTcov: Mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            let sum = 0;
            for (let k = 0; k < 3; k++) {
                sum += cov[k * 3 + i] * cov[k * 3 + j];
            }
            covTcov[i * 3 + j] = sum;
        }
    }
    const { values, vectors: V } = symmetricEigen3(covTcov);
    const sv = values.map(x => Math.sqrt(Math.max(x, 0))) as [number, number, number];

    // Rank threshold: eigenvalues of cov^T cov are only accurate to about
    // machine epsilon relative to the largest, so singular values (their
    // square roots) carry ~sqrt(eps) ≈ 1e-8 relative noise. Anything below
    // this cutoff is indistinguishable from zero and must not be divided by.
    const RANK_EPS = 1e-6;

    // The source points must span at least a plane, otherwise the rotation
    // about the spanned line is undetermined. (Rank exactly 2 — the minimum
    // three-non-collinear-points case — is fine.)
    if (sv[1] <= RANK_EPS * Math.max(sv[0], 1e-300)) {
        return { error: 'degenerate' };
    }
    if (sv[2] <= RANK_EPS * sv[0]) {
        sv[2] = 0;
    }

    // U columns: u_i = cov * v_i / sv_i for the well-conditioned columns,
    // third column completed as u0 x u1 (keeps U orthonormal at rank 2).
    const U: Mat3 = new Array(9).fill(0);
    for (let col = 0; col < 2; col++) {
        for (let row = 0; row < 3; row++) {
            let sum = 0;
            for (let k = 0; k < 3; k++) {
                sum += cov[row * 3 + k] * V[k * 3 + col];
            }
            U[row * 3 + col] = sum / sv[col];
        }
    }
    if (sv[2] > 0) {
        for (let row = 0; row < 3; row++) {
            let sum = 0;
            for (let k = 0; k < 3; k++) {
                sum += cov[row * 3 + k] * V[k * 3 + 2];
            }
            U[row * 3 + 2] = sum / sv[2];
        }
    } else {
        U[2] = U[3] * U[7] - U[6] * U[4];
        U[5] = U[6] * U[1] - U[0] * U[7];
        U[8] = U[0] * U[4] - U[3] * U[1];
    }

    // reflection guard: R = U * diag(1, 1, d) * V^T with d = ±1 so det(R) = +1
    const d = det3(U) * det3(V) < 0 ? -1 : 1;
    const R: Mat3 = new Array(9);
    for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 3; col++) {
            let sum = 0;
            for (let k = 0; k < 3; k++) {
                const dk = k === 2 ? d : 1;
                sum += U[row * 3 + k] * dk * V[col * 3 + k];
            }
            R[row * 3 + col] = sum;
        }
    }

    const scale = withScale ? (sv[0] + sv[1] + d * sv[2]) / sigmaSrc : 1;

    const rMu = mulMat3Point(R, muSrc);
    const translation: Point3 = [
        muDst[0] - scale * rMu[0],
        muDst[1] - scale * rMu[1],
        muDst[2] - scale * rMu[2]
    ];

    const result: SimilarityTransform = { rotation: R, scale, translation, rmse: 0, residuals: [] };
    let sq = 0;
    for (let i = 0; i < n; i++) {
        const p = applySimilarity(result, src[i]);
        const e = Math.hypot(p[0] - dst[i][0], p[1] - dst[i][1], p[2] - dst[i][2]);
        result.residuals.push(e);
        sq += e * e / n;
    }
    result.rmse = Math.sqrt(sq);

    return result;
};

export { estimateSimilarity, applySimilarity, isSimilarityError, det3, mulMat3Point };
export type { Point3, Mat3, SimilarityTransform, SimilarityError, SimilarityResult };
